import type { Contact_TasksFilter } from "@/src/gql/graphql";

type GraphQlFilter = Record<string, unknown>;

interface BuildPresetUserFilterArgs {
  entityName: string;
  userId: string;
}

/**
 * Build the GraphQL filter used by the shared "My {Entity}" preset.
 * Tasks are considered assigned when the user is either the creator/owner
 * (user_id) or included in assigned_users.
 */
export function buildPresetUserFilter({
  entityName,
  userId,
}: BuildPresetUserFilterArgs): GraphQlFilter {
  if (entityName === "tasks") {
    const taskAssignmentFilter: Contact_TasksFilter = {
      or: [
        { user_id: { eq: userId } },
        { assigned_users: { contains: [userId] } },
      ],
    };

    return taskAssignmentFilter as GraphQlFilter;
  }

  return { user_id: { eq: userId } };
}

/**
 * Combine two GraphQL filter objects while preserving existing predicates.
 */
export function mergeFiltersWithAnd(
  baseFilter: GraphQlFilter,
  additionalFilter: GraphQlFilter
): GraphQlFilter {
  if (Object.keys(baseFilter).length === 0) {
    return additionalFilter;
  }

  if (Object.keys(additionalFilter).length === 0) {
    return baseFilter;
  }

  const existingAnd = baseFilter.and;
  if (Array.isArray(existingAnd)) {
    return {
      ...baseFilter,
      and: [...existingAnd, additionalFilter],
    };
  }

  return {
    and: [baseFilter, additionalFilter],
  };
}

/**
 * Resolve saved-view token values (e.g. "me") in GraphQL operator payloads.
 * This keeps saved views portable while still querying with concrete IDs.
 */
export function resolveCurrentUserFilterTokens(
  filter: GraphQlFilter,
  userId: string | null | undefined
): GraphQlFilter {
  if (!userId) {
    return filter;
  }

  // Every operator that compares against a USER ID and can therefore carry the
  // "me" token. `in` belongs here: a multi-select saved view serialises to
  // `{ assigned_to: { in: ["me"] } }` (see `convertArrayValueToGraphQL`), and
  // leaving it out queried for the literal string "me" — a silently EMPTY list
  // rather than the rep's own records.
  const USER_ID_OPERATORS = ["eq", "neq", "in", "contains", "overlaps"];

  const transformValue = (value: unknown, keyHint?: string): unknown => {
    if (typeof value === "string") {
      if (keyHint && USER_ID_OPERATORS.includes(keyHint) && value === "me") {
        return userId;
      }
      return value;
    }

    if (Array.isArray(value)) {
      return value.map((item) => transformValue(item, keyHint));
    }

    if (typeof value === "object" && value !== null) {
      const out: Record<string, unknown> = {};
      Object.entries(value as Record<string, unknown>).forEach(([key, nested]) => {
        out[key] = transformValue(nested, key);
      });
      return out;
    }

    return value;
  };

  return transformValue(filter) as GraphQlFilter;
}
